import equal from 'fast-deep-equal';
import {associationMap} from '@src/configs/label_styles_config';

export class Label {

  public static readonly TOP_LEFT      = 'TOP_LEFT';
  public static readonly TOP_CENTER    = 'TOP_CENTER';
  public static readonly TOP_RIGHT     = 'TOP_RIGHT';

  public static readonly CENTER_LEFT   = 'CENTER_LEFT';
  public static readonly CENTER        = 'CENTER';
  public static readonly CENTER_RIGHT  = 'CENTER_RIGHT';

  public static readonly BOTTOM_LEFT   = 'BOTTOM_LEFT';
  public static readonly BOTTOM_CENTER = 'BOTTOM_CENTER';
  public static readonly BOTTOM_RIGHT  = 'BOTTOM_RIGHT';

  private static readonly OFFSET_LAT_LNG = {lat: 0.00015, lng: 0.00015};

  public static readonly scale = 0.5;

  protected config = undefined;
  protected latLng = undefined;
  private value = undefined;
  private anchor = undefined;
  private style = undefined;
  private drawFunction = undefined;


  private associationMap = associationMap;

  constructor(config) {
    this.config = config;
    this.latLng = this.parseLatLng();
    this.parseConfig();
  }

  public static getIdStatic(config) {
    return config.id;
  }

  public isEmpty() {
    return this.value == '';
  }

  public isEqual(item) {
    return equal(this.config, item.config);
  }

  public isEqualGraphic(item) {
    return equal(this.value, item.value) && equal(this.style, item.style);
  }

  public getGraphicDataAsString() {
    return JSON.stringify(this.value) + ' || ' + JSON.stringify(this.style);
  }

  public getConfig() {
    return this.config;
  }

  public getId() {
    return this.config.id;
  }

  public getLatLng() {
    return this.latLng;
  }

  public getAnchor() {
    return this.anchor;
  }

  public getAnchorOffset(latLang) {
    let lng = 0;
    let lat = 0;

    if (this.anchor === Label.TOP_LEFT || this.anchor === Label.CENTER_LEFT || this.anchor === Label.BOTTOM_LEFT) {
      lng = -Label.OFFSET_LAT_LNG.lng;
    }
    if (this.anchor === Label.TOP_RIGHT || this.anchor === Label.CENTER_RIGHT || this.anchor === Label.BOTTOM_RIGHT) {
      lng = Label.OFFSET_LAT_LNG.lng;
    }
    if (this.anchor === Label.TOP_LEFT || this.anchor === Label.TOP_CENTER || this.anchor === Label.TOP_RIGHT) {
      lat = Label.OFFSET_LAT_LNG.lat;
    }
    if (this.anchor === Label.BOTTOM_LEFT || this.anchor === Label.BOTTOM_CENTER || this.anchor === Label.BOTTOM_RIGHT) {
      lat = -Label.OFFSET_LAT_LNG.lat;
    }

    return {lat: latLang.lat + lat, lng: latLang.lng + lng};
  }

  private resize(n) {
    return Math.round(n / Label.scale * 100) / 100;
  }

  private getStyleFunction(style) {
    if (style){
      style = style.toLowerCase();
    }

    if (this.associationMap.has(style)){
      style = this.associationMap.get(style).toLowerCase();
    }

    switch (style) {
      case 'black large':
        return this.drawBlackBackgroundLarge;
      case 'white large':
        return this.drawWhiteBackgroundLarge;
      case 'black small':
        return this.drawBlackBackgroundSmall;
      case 'white small':
        return this.drawWhiteBackgroundSmall;

      case 'white large - shadow':
        return this.drawWhiteBackgroundLargeShadow;
      case 'white small - shadow':
        return this.drawWhiteBackgroundSmallShadow;

      case 'primary':
        return this.drawPrimary;
      case 'white text':
        return this.drawVanilla;
      case 'black text':
        return this.drawEbony;
      case 'oval large':
        return this.drawBigDefault;
      case 'oval small':
        return this.drawDefault;
    }
    return this.drawBlackBackgroundLarge;
  }

  public draw(context, isCalcSizeOnly: boolean = false) {
    return this.drawFunction(context, isCalcSizeOnly);
  }

  private drawBlackBackgroundLarge(context, isCalcSizeOnly) {
    return this.drawBackgroundLarge(context, 'rgba(0, 0, 0, 0.6)', 'rgb(255, 255, 255)', false, isCalcSizeOnly);
  }

  private drawWhiteBackgroundLarge(context, isCalcSizeOnly) {
    return this.drawBackgroundLarge(context, 'rgba(255, 255, 255, 0.6)', 'rgb(0, 0, 0)', false, isCalcSizeOnly);
  }

  private drawBackgroundLarge(context, backgroundColor, color, isDrawShadow, isCalcSizeOnly) {
    context.font = `600 ${this.resize(16)}px Poppins, sans-serif`;
    const text = this.config.value;
    const textWidth = context.measureText(text).width;

    const xy = isDrawShadow
      ? {x: textWidth + this.resize(40), y: this.resize(38)}
      : {x: textWidth + this.resize(36), y: this.resize(34)};

    if (!isCalcSizeOnly) {
      context.save();
      if (isDrawShadow) {
        this.setContextShadow(context);
      }

      context.beginPath();
      context.fillStyle = backgroundColor;
      context.roundRect(
        this.resize(1), this.resize(1),
        textWidth + this.resize(32), this.resize(32),
        [this.resize(3)]
      );
      context.fill();
      context.restore();

      context.fillStyle = color;
      context.textBaseline = 'middle';
      context.textAlign = 'center';
      context.fillText(text, textWidth / 2 + this.resize(16) + this.resize(1), this.resize(16) + this.resize(1));
    }

    return xy;
  }

  private drawBlackBackgroundSmall(context, isCalcSizeOnly) {
    return this.drawBackgroundSmall(context, 'rgba(0, 0, 0, 0.6)', 'rgb(255, 255, 255)', false, isCalcSizeOnly);
  }

  private drawWhiteBackgroundSmall(context, isCalcSizeOnly) {
    return this.drawBackgroundSmall(context, 'rgba(255, 255, 255, 0.6)', 'rgb(0, 0, 0)', false, isCalcSizeOnly);
  }

  private drawBackgroundSmall(context, backgroundColor, color, isDrawShadow, isCalcSizeOnly) {
    context.font = `600 ${this.resize(12)}px Poppins, sans-serif`;
    const text = this.config.value;
    const textWidth = context.measureText(text).width;

    const xy = isDrawShadow
      ? {x: textWidth + this.resize(24), y: this.resize(30)}
      : {x: textWidth + this.resize(20), y: this.resize(26)};


    if (!isCalcSizeOnly) {
      context.save();
      if (isDrawShadow) {
        this.setContextShadow(context);
      }

      context.beginPath();
      context.fillStyle = backgroundColor;
      context.roundRect(
        this.resize(1), this.resize(1),
        textWidth + this.resize(16), this.resize(24),
        [this.resize(3)]
      );
      context.fill();
      context.restore();

      context.fillStyle = color;
      context.textBaseline = 'middle';
      context.textAlign = 'center';
      context.fillText(text, textWidth / 2 + this.resize(8) + this.resize(1), this.resize(12) + this.resize(1));
    }

    return xy;
  }

  private setContextShadow(context) {
    context.shadowColor = 'rgba(0, 0, 0, 0.25)';
    context.shadowOffsetY = this.resize(4);
    context.shadowOffsetX = this.resize(4);
  }

  private drawWhiteBackgroundLargeShadow(context, isCalcSizeOnly) {
    return this.drawBackgroundLarge(context, 'rgba(255, 255, 255)', 'rgb(0, 0, 0)', true, isCalcSizeOnly);
  }

  private drawWhiteBackgroundSmallShadow(context, isCalcSizeOnly) {
    return this.drawBackgroundSmall(context, 'rgba(255, 255, 255)', 'rgb(0, 0, 0)', true, isCalcSizeOnly);
  }

  private drawPrimary(context, isCalcSizeOnly) {
    context.font = `600 ${this.resize(16)}px Poppins, sans-serif`;
    const text = this.config.value;
    const textWidth = context.measureText(text).width;

    const xy = {x: textWidth + this.resize(36), y: this.resize(34)};

    if (!isCalcSizeOnly) {
      context.beginPath();
      context.fillStyle = 'rgb(33, 33, 33)';
      context.fillRect(
        this.resize(1), this.resize(1),
        textWidth + this.resize(32), this.resize(32),
      );

      context.fill();

      context.fillStyle = 'rgb(231, 228, 223)';
      context.textBaseline = 'middle';
      context.textAlign = 'center';
      context.fillText(text, textWidth / 2 + this.resize(16) + this.resize(1), this.resize(16) + this.resize(1));
    }

    return xy;
  }

  private drawVanilla(context, isCalcSizeOnly) {
    return this.drawText(context, 'white', isCalcSizeOnly);
  }

  private drawEbony(context, isCalcSizeOnly) {
    return this.drawText(context, 'black', isCalcSizeOnly);
  }

  private drawText(context, color, isCalcSizeOnly) {
    context.font = `600 ${this.resize(16)}px Poppins, sans-serif`;
    const text = this.config.value;
    const textWidth = context.measureText(text).width;

    const xy = {
      x: textWidth + this.resize(4),
      y: this.resize(18)
    };

    if (!isCalcSizeOnly) {
      context.fillStyle = color;
      context.textBaseline = 'middle';
      context.textAlign = 'center';
      context.fillText(text, textWidth / 2 + this.resize(1), this.resize(8) + this.resize(1));
    }

    return xy;
  }

  private drawBigDefault(context, isCalcSizeOnly) {
    context.font = `600 ${this.resize(18)}px Poppins, sans-serif`;
    const text = this.config.value;
    const textWidth = context.measureText(text).width;

    const xy = {x: textWidth + this.resize(50), y: this.resize(48)};

    if (!isCalcSizeOnly) {
      context.beginPath();
      context.fillStyle = 'rgb(233, 233, 233)';
      context.ellipse(
        textWidth / 2 + this.resize(24) + this.resize(1), this.resize(24) + this.resize(1),
        textWidth / 2 + this.resize(24), this.resize(24),
        0, 0, 2 * Math.PI
      );
      context.fill();

      context.fillStyle = 'rgb(33, 37, 41)';
      context.textBaseline = 'middle';
      context.textAlign = 'center';
      context.fillText(text, textWidth / 2 + this.resize(24) + this.resize(1), this.resize(24) + this.resize(1));
    }

    return xy;
  }

  private drawDefault(context, isCalcSizeOnly) {
    context.font = `600 ${this.resize(16)}px Poppins, sans-serif`;
    const text = this.config.value;
    const textWidth = context.measureText(text).width;

    const xy = {x: textWidth + this.resize(44), y: this.resize(42)};

    if (!isCalcSizeOnly) {
      context.beginPath();
      context.fillStyle = 'rgb(233, 233, 233)';
      context.ellipse(
        textWidth / 2 + this.resize(20) + this.resize(1), this.resize(20) + this.resize(1),
        textWidth / 2 + this.resize(20), this.resize(20),
        0, 0, 2 * Math.PI
      );
      context.fill();

      context.fillStyle = 'rgb(33, 37, 41)';
      context.textBaseline = 'middle';
      context.textAlign = 'center';
      context.fillText(text, textWidth / 2 + this.resize(20) + this.resize(1), this.resize(20) + this.resize(1));
    }

    return xy;
  }

  protected parseLatLng() {
    if ( typeof this.config.position === 'undefined'
      || typeof this.config.position.latitude === 'undefined'
      || typeof this.config.position.longitude === 'undefined'
    ) {
      throw new Error('Invalid position format');
    }

    return { lat: this.config.position.latitude, lng: this.config.position.longitude };
  }

  protected parseConfig() {
    if ( typeof this.config.value === 'undefined' || typeof this.config.anchor === 'undefined') {
      throw new Error('Invalid config format');
    }

    this.value = this.config.value;
    this.anchor = this.config.anchor;

    this.style = this.associationMap.get('default').toLowerCase();
    this.drawFunction = this.drawBlackBackgroundLarge;
    if (typeof this.config.style !== 'undefined' && this.config.style) {
      this.style = this.config.style;
      this.drawFunction = this.getStyleFunction(this.config.style);
    }
  }
}

/*
To test in sandbox possibler to use next code:

this.resize = function(size) { return size; }
this.config = {value: 'Test text'};
isCalcSizeOnly = false;
color = 'rgb(0, 0, 0)';
backgroundColor = 'rgba(255, 255, 255, 0.6)';
...
*/
